<template>
  <header class="mnt-topbar">
    <div v-if="loading" class="mnt-topbar__status">Cargando...</div>

    <template v-else-if="park">
      <div class="mnt-topbar__left">
        <span class="mnt-topbar__section">Mantenimiento</span>
        <span class="mnt-topbar__park-name">{{ park.name }}</span>
      </div>

      <div class="mnt-topbar__divider"></div>

      <div class="mnt-topbar__search-wrap">
        <div class="mnt-topbar__search" :class="{ 'mnt-topbar__search--active': searchActive }">
          <span class="mdi mdi-magnify mnt-topbar__search-icon"></span>
          <input
            ref="searchInputEl"
            :value="search"
            type="text"
            class="mnt-topbar__search-input"
            placeholder="Buscar ticket, nave o inquilino..."
            @focus="searchFocused = true"
            @blur="onSearchBlur"
            @input="$emit('update:search', $event.target.value)"
          />
          <button v-if="search" class="mnt-topbar__search-clear" tabindex="-1" @mousedown.prevent="clearSearch">
            <span class="mdi mdi-close"></span>
          </button>
        </div>

        <div v-if="showDropdown" class="mnt-topbar__dropdown">
          <div v-if="searchLoading" class="mnt-topbar__dropdown-loading">Buscando...</div>
          <template v-else-if="hasResults">
            <div v-for="group in resultGroups" :key="group.key" class="mnt-topbar__dropdown-group">
              <div class="mnt-topbar__dropdown-label">{{ group.label }}</div>
              <button
                v-for="item in group.items"
                :key="item.id"
                class="mnt-topbar__dropdown-item"
                @mousedown.prevent="selectResult(group.key, item)"
              >
                <span class="mnt-topbar__dropdown-item-name">{{ item.name }}</span>
                <span v-if="item.context" class="mnt-topbar__dropdown-item-context">{{ item.context }}</span>
              </button>
            </div>
          </template>
          <div v-else class="mnt-topbar__dropdown-empty">Sin resultados</div>
        </div>
      </div>

      <div class="mnt-topbar__divider"></div>

      <div v-if="activeTab !== 'preventivo'" class="mnt-topbar__type-pills">
        <button
          v-for="type in TYPE_PILLS"
          :key="type.key"
          class="mnt-topbar__type-pill"
          :class="[`mnt-topbar__type-pill--${type.key}`, { 'mnt-topbar__type-pill--active': typeFilters.includes(type.key) }]"
          @click="toggleType(type.key)"
        >
          {{ type.label }}
        </button>
      </div>

      <div class="mnt-topbar__spacer"></div>

      <div class="mnt-topbar__divider"></div>

      <button class="mnt-topbar__new-btn" @click="$emit('new-request')">
        <span class="mdi mdi-plus"></span>
        Nueva solicitud
      </button>

      <div class="mnt-topbar__divider"></div>

      <GlobalAlertsChip :park-id="park?.id" />
      <NotificationBell :park-id="park?.id" />
    </template>
  </header>

  <div v-if="park" class="mnt-statusbar">
    <div class="mnt-statusbar__tabs">
      <button
        v-for="tab in STATUS_TABS"
        :key="tab.key"
        class="mnt-statusbar__tab"
        :class="{ 'mnt-statusbar__tab--active': activeTab === tab.key }"
        @click="$emit('update:activeTab', tab.key)"
      >
        <span v-if="tab.dot" class="mnt-statusbar__tab-dot" :class="`mnt-statusbar__tab-dot--${tab.dot}`"></span>
        <span v-if="tab.icon" class="mdi mnt-statusbar__tab-icon" :class="tab.icon"></span>
        <span class="mnt-statusbar__tab-label">{{ tab.label }}</span>
        <span
          v-if="tab.countKey"
          class="mnt-statusbar__tab-count"
          :class="tab.key === 'abiertos' && counts.hasUrgentUnassigned ? 'mnt-statusbar__tab-count--red' : ''"
        >({{ counts[tab.countKey] ?? 0 }})</span>
      </button>
    </div>

    <div v-if="technicians.length > 0 && activeTab !== 'preventivo'" class="mnt-statusbar__technicians">
      <button
        v-for="t in visibleTechnicians"
        :key="t.id"
        class="mnt-statusbar__avatar"
        :class="{ 'mnt-statusbar__avatar--active': technicianFilters.includes(t.id) }"
        :style="{ background: AVATAR_COLORS[t.avatarColor] || t.avatarColor }"
        @click="toggleTechnician(t.id)"
        @mouseenter="onAvatarHover(t, $event)"
        @mouseleave="hoveredTechnicianId = null"
      >
        {{ initials(t.name) }}
      </button>
      <button
          class="mnt-statusbar__manage-btn"
          :class="{ 'mnt-statusbar__manage-btn--active': technicianManagerOpen }"
          @click="$emit('open-technician-manager')"
        >
          <span class="mdi mdi-plus"></span>
        </button>
      <div v-if="overflowTechnicians.length" class="mnt-statusbar__avatar-overflow-wrap">
        <button class="mnt-statusbar__avatar mnt-statusbar__avatar--overflow" @click="overflowOpen = !overflowOpen">
          +{{ overflowTechnicians.length }}
        </button>
        <div v-if="overflowOpen" class="mnt-statusbar__overflow-dropdown">
          <button
            v-for="t in overflowTechnicians"
            :key="t.id"
            class="mnt-statusbar__overflow-item"
            :class="{ 'mnt-statusbar__overflow-item--active': technicianFilters.includes(t.id) }"
            @click="toggleTechnician(t.id)"
          >
            <span class="mnt-statusbar__avatar mnt-statusbar__avatar--small" :style="{ background: AVATAR_COLORS[t.avatarColor] || t.avatarColor }">
              {{ initials(t.name) }}
            </span>
            {{ t.name }}
          </button>
        </div>
      </div>

      <Teleport to="body">
        <div v-if="hoveredTechnicianId" class="mnt-statusbar__tech-tooltip" :style="{ top: tooltipPos.top + 'px', left: tooltipPos.left + 'px' }">
          <div class="mnt-statusbar__tech-tooltip-name">{{ hoveredTechnician?.name }}</div>
          <div class="mnt-statusbar__tech-tooltip-line">{{ hoveredTechnician?.activeTicketCount }} tickets activos</div>
          <div class="mnt-statusbar__tech-tooltip-line">
            {{ hoveredTechnician?.avgResolutionHours !== null ? `${hoveredTechnician.avgResolutionHours}h prom. resolución` : 'Sin resoluciones recientes' }}
          </div>
        </div>
      </Teleport>
    </div>
  </div>

  <div v-if="park && activeFiltersCount > 0" class="mnt-active-filters">
    <span v-if="search" class="mnt-active-filters__tag" @click="$emit('update:search', '')">
      Búsqueda: "{{ search }}" <span class="mdi mdi-close"></span>
    </span>
    <span
      v-for="typeKey in typeFilters"
      :key="typeKey"
      class="mnt-active-filters__tag"
      @click="toggleType(typeKey)"
    >
      {{ TYPE_PILLS.find((t) => t.key === typeKey)?.label }} <span class="mdi mdi-close"></span>
    </span>
    <span
      v-for="techId in technicianFilters"
      :key="techId"
      class="mnt-active-filters__tag"
      @click="toggleTechnician(techId)"
    >
      {{ technicians.find((t) => t.id === techId)?.name }} <span class="mdi mdi-close"></span>
    </span>
    <button class="mnt-active-filters__clear" @click="clearAllFilters">Limpiar todos</button>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import NotificationBell from '@/components/Global/NotificationBell.vue'
import GlobalAlertsChip from '@/components/Global/GlobalAlertsChip.vue'

const props = defineProps({
  park: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  alerts: { type: Array, default: () => [] },
  technicianManagerOpen: { type: Boolean, default: false },

  activeTab: { type: String, default: 'abiertos' },
  counts: {
    type: Object,
    default: () => ({ abiertos: 0, enProceso: 0, enEspera: 0, resueltos: 0, todos: 0, hasUrgentUnassigned: false }),
  },

  typeFilters: { type: Array, default: () => [] },
  technicianFilters: { type: Array, default: () => [] },
  technicians: { type: Array, default: () => [] },

  view: { type: String, default: 'kanban' },

  search: { type: String, default: '' },
  searchResults: {
    type: Object,
    default: () => ({ activos: [], resueltos: [], porNave: [], porTecnico: [] }),
  },
  searchLoading: { type: Boolean, default: false },
})

const emit = defineEmits([
  'update:activeTab',
  'update:typeFilters',
  'update:technicianFilters',
  'update:view',
  'update:search',
  'search-select',
  'new-request',
  'open-notifications',
  'open-technician-manager',
])

const AVATAR_COLORS = {
  blue: '#3b82f6', green: '#10b981', violet: '#8b5cf6', orange: '#f97316',
  teal: '#14b8a6', pink: '#ec4899', indigo: '#6366f1', amber: '#f0a500',
}

const STATUS_TABS = [
  { key: 'abiertos', label: 'Abiertos', countKey: 'abiertos', dot: 'red' },
  { key: 'enProceso', label: 'En proceso', countKey: 'enProceso' },
  { key: 'enEspera', label: 'En espera', countKey: 'enEspera', dot: 'amber' },
  { key: 'resueltos', label: 'Resueltos', countKey: 'resueltos' },
  { key: 'todos', label: 'Todos', countKey: 'todos' },
  { key: 'preventivo', label: 'Preventivo', icon: 'mdi-calendar-month-outline' },
]

const TYPE_PILLS = [
  { key: 'CORRECTIVE', label: 'Correctivo' },
  { key: 'PREVENTIVE', label: 'Preventivo' },
  { key: 'IMPROVEMENT', label: 'Mejora' },
]

function toggleType(key) {
  const next = props.typeFilters.includes(key)
    ? props.typeFilters.filter((k) => k !== key)
    : [...props.typeFilters, key]
  emit('update:typeFilters', next)
}

function toggleTechnician(id) {
  const next = props.technicianFilters.includes(id)
    ? props.technicianFilters.filter((k) => k !== id)
    : [...props.technicianFilters, id]
  emit('update:technicianFilters', next)
}

function clearAllFilters() {
  emit('update:search', '')
  emit('update:typeFilters', [])
  emit('update:technicianFilters', [])
}

const activeFiltersCount = computed(
  () => (props.search ? 1 : 0) + props.typeFilters.length + props.technicianFilters.length
)

// --- Buscador ---
const searchInputEl = ref(null)
const searchFocused = ref(false)

const searchActive = computed(() => searchFocused.value || !!props.search)
const showDropdown = computed(() => searchFocused.value && props.search.length > 0)

const resultGroups = computed(() => {
  const groups = [
    { key: 'activos', label: 'Tickets activos', items: props.searchResults.activos || [] },
    { key: 'resueltos', label: 'Tickets resueltos', items: props.searchResults.resueltos || [] },
    { key: 'porNave', label: 'Por nave', items: props.searchResults.porNave || [] },
    { key: 'porTecnico', label: 'Por técnico', items: props.searchResults.porTecnico || [] },
  ]
  return groups.filter((g) => g.items.length > 0)
})

const hasResults = computed(() => resultGroups.value.length > 0)

function onSearchBlur() {
  setTimeout(() => { searchFocused.value = false }, 100)
}

function clearSearch() {
  emit('update:search', '')
  searchInputEl.value?.focus()
}

function selectResult(groupKey, item) {
  emit('search-select', { type: groupKey, item })
  searchFocused.value = false
}

// --- Técnicos ---
const MAX_VISIBLE_TECHNICIANS = 5
const visibleTechnicians = computed(() => props.technicians.slice(0, MAX_VISIBLE_TECHNICIANS))
const overflowTechnicians = computed(() => props.technicians.slice(MAX_VISIBLE_TECHNICIANS))
const overflowOpen = ref(false)

function initials(name) {
  return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
}

const hoveredTechnicianId = ref(null)
const tooltipPos = ref({ top: 0, left: 0 })

const hoveredTechnician = computed(() => props.technicians.find((t) => t.id === hoveredTechnicianId.value) || null)

function onAvatarHover(t, event) {
  hoveredTechnicianId.value = t.id
  const rect = event.currentTarget.getBoundingClientRect()
  tooltipPos.value = { top: rect.bottom + 6, left: rect.left }
}

</script>

<style scoped>
.mnt-topbar {
  display: flex;
  align-items: center;
  padding: 0.6rem 1.5rem;
  background: var(--side-panel);
  border-left: 1px solid var(--border-subtle);
  gap: 0.75rem;
  height: 56px;
  box-sizing: border-box;
}

.mnt-topbar__status {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.mnt-topbar__divider {
  width: 1px;
  align-self: stretch;
  background: rgba(255, 255, 255, 0.15);
  flex-shrink: 0;
}

.mnt-topbar__spacer {
  flex: 1;
}

.mnt-topbar__left {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.mnt-topbar__section {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 15px;
  color: #ffffff;
}

.mnt-topbar__park-name {
  font-family: var(--font-body);
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
}

.mnt-topbar__search-wrap {
  position: relative;
  flex: 1 1 180px;
  min-width: 140px;
  max-width: 220px;
}

.mnt-topbar__search {
  display: flex;
  align-items: center;
  height: 32px;
  background: #24487a;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 0 0.5rem;
  gap: 0.35rem;
  transition: border-color 150ms ease;
}

.mnt-topbar__search--active {
  border-color: rgba(255, 255, 255, 0.6);
}

.mnt-topbar__search-icon {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
  flex-shrink: 0;
}

.mnt-topbar__search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-family: var(--font-body);
  font-size: 12px;
  min-width: 0;
}

.mnt-topbar__search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.mnt-topbar__search-clear {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  display: flex;
  font-size: 0.8rem;
}

.mnt-topbar__dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.15);
  max-height: 360px;
  overflow-y: auto;
  z-index: 200;
  min-width: 260px;
}

.mnt-topbar__dropdown-loading,
.mnt-topbar__dropdown-empty {
  padding: 0.6rem;
  font-size: 0.76rem;
  color: #64748b;
  text-align: center;
}

.mnt-topbar__dropdown-group + .mnt-topbar__dropdown-group {
  border-top: 1px solid #e2e8f0;
}

.mnt-topbar__dropdown-label {
  padding: 0.4rem 0.6rem 0.2rem;
  font-family: var(--font-body);
  font-size: 0.64rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
}

.mnt-topbar__dropdown-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 0.35rem 0.6rem;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
}

.mnt-topbar__dropdown-item:hover {
  background: #f8fafc;
}

.mnt-topbar__dropdown-item-name {
  font-family: var(--font-body);
  font-size: 0.8rem;
  font-weight: 500;
  color: #1e3a5f;
}

.mnt-statusbar__manage-btn {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px dashed #cbd5e1;
  background: #ffffff;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 0.4rem;
  flex-shrink: 0;
  font-size: 13px;
}

.mnt-statusbar__manage-btn:hover {
  border-color: #3b82f6;
  color: #3b82f6;
}

.mnt-topbar__dropdown-item-context {
  font-family: var(--font-body);
  font-size: 0.7rem;
  color: #64748b;
}

.mnt-topbar__type-pills {
  display: flex;
  gap: 0.25rem;
  flex-shrink: 0;
}

.mnt-topbar__type-pill {
  background: transparent;
  border: 1px solid;
  border-radius: 20px;
  padding: 0.15rem 0.5rem;
  font-family: var(--font-body);
  font-size: 0.65rem;
  cursor: pointer;
  white-space: nowrap;
}

.mnt-topbar__type-pill--CORRECTIVE { border-color: rgba(239, 68, 68, 0.4); color: rgba(255, 255, 255, 0.6); }
.mnt-topbar__type-pill--PREVENTIVE { border-color: rgba(59, 130, 246, 0.4); color: rgba(255, 255, 255, 0.6); }
.mnt-topbar__type-pill--IMPROVEMENT { border-color: rgba(139, 92, 246, 0.4); color: rgba(255, 255, 255, 0.6); }

.mnt-topbar__type-pill--CORRECTIVE.mnt-topbar__type-pill--active { background: #fee2e2; border-color: #ef4444; color: #dc2626; }
.mnt-topbar__type-pill--PREVENTIVE.mnt-topbar__type-pill--active { background: #dbeafe; border-color: #3b82f6; color: #2563eb; }
.mnt-topbar__type-pill--IMPROVEMENT.mnt-topbar__type-pill--active { background: #ede9fe; border-color: #8b5cf6; color: #7c3aed; }

.mnt-topbar__view-toggle {
  display: flex;
  flex-shrink: 0;
}

.mnt-topbar__view-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  font-size: 15px;
  transition: opacity 200ms ease;
}

.mnt-topbar__view-btn:hover {
  color: rgba(255, 255, 255, 0.6);
}

.mnt-topbar__view-btn--left { border-radius: 6px 0 0 6px; }
.mnt-topbar__view-btn--right { border-radius: 0 6px 6px 0; }

.mnt-topbar__view-btn--active {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.mnt-topbar__new-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  height: 32px;
  background: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 0 0.65rem;
  color: #1e3a5f;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
}

.mnt-topbar__new-btn:hover {
  background: #f1f5f9;
}

.mnt-topbar__notif {
  position: relative;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
}

.mnt-topbar__notif-badge {
  position: absolute;
  top: -6px;
  right: -8px;
  background: var(--accent-blue);
  color: white;
  font-size: 0.6rem;
  font-family: var(--font-mono);
  padding: 0 4px;
  border-radius: 8px;
  min-width: 14px;
  text-align: center;
}

.mnt-topbar__notif-badge--critical {
  background: var(--accent-red);
}

/* Barra de tabs de estatus + técnicos, debajo del topbar */
.mnt-statusbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1.5rem;
  background: transparent;
}

.mnt-statusbar__tabs {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.mnt-statusbar__tab {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  background: transparent;
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.65rem;
  cursor: pointer;
  position: relative;
  transition: background 100ms ease;
}

.mnt-statusbar__tab:hover {
  background: rgba(0, 0, 0, 0.03);
}

.mnt-statusbar__tab-label {
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: var(--text-content);
}

.mnt-statusbar__tab-count {
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: #94a3b8;
}

.mnt-statusbar__tab--active {
  background: #eff6ff;
}

.mnt-statusbar__tab--active::after {
  content: '';
  position: absolute;
  left: 0.5rem;
  right: 0.5rem;
  bottom: -1px;
  height: 2px;
  background: #1e3a5f;
  border-radius: 2px;
}

.mnt-statusbar__tab--active .mnt-statusbar__tab-label {
  color: #1e3a5f;
  font-weight: 500;
}

.mnt-statusbar__tab-count--red {
  color: #dc2626 !important;
}

.mnt-statusbar__tab-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.mnt-statusbar__tab-dot--red { background: #ef4444; }
.mnt-statusbar__tab-dot--amber { background: #f59e0b; }

.mnt-statusbar__tab-icon {
  font-size: 12px;
  color: #64748b;
}

.mnt-statusbar__tab--active .mnt-statusbar__tab-icon {
  color: #1e3a5f;
}

.mnt-statusbar__technicians {
  display: flex;
  align-items: center;
  gap: 0;
  flex-shrink: 0;
  position: relative;
}

.mnt-statusbar__avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  color: #ffffff;
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: -8px;
  position: relative;
  transition: transform 150ms ease, z-index 0s;
}

.mnt-statusbar__avatar:first-child {
  margin-left: 0;
}

.mnt-statusbar__avatar:hover {
  z-index: 10;
  transform: scale(1.1);
}

.mnt-statusbar__avatar--active {
  border-color: #1e3a5f;
}

.mnt-statusbar__avatar--overflow {
  background: #e2e8f0;
  color: #64748b;
  font-size: 9px;
}

.mnt-statusbar__avatar--small {
  width: 20px;
  height: 20px;
  font-size: 8px;
}

.mnt-statusbar__avatar-overflow-wrap {
  position: relative;
}

.mnt-statusbar__overflow-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.15);
  padding: 0.3rem;
  z-index: 200;
  min-width: 180px;
}

.mnt-statusbar__overflow-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  background: none;
  border: none;
  padding: 0.35rem 0.4rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--text-content);
  cursor: pointer;
  border-radius: 5px;
  text-align: left;
}

.mnt-statusbar__overflow-item:hover {
  background: #f8fafc;
}

.mnt-statusbar__overflow-item--active {
  background: #eff6ff;
}

.mnt-statusbar__tech-tooltip {
  position: fixed;
  background: #1e3a5f;
  color: #ffffff;
  border-radius: 8px;
  padding: 0.5rem 0.65rem;
  font-family: var(--font-body);
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  min-width: 160px;
}

.mnt-statusbar__tech-tooltip-name {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 0.2rem;
}

.mnt-statusbar__tech-tooltip-line {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
}

/* Tira de filtros activos */
.mnt-active-filters {
  height: 24px;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 1.5rem;
  background: #f0f4ff;
}

.mnt-active-filters__tag {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-family: var(--font-body);
  font-size: 10px;
  color: #1e3a5f;
  background: #ffffff;
  border: 1px solid #dbeafe;
  border-radius: 4px;
  padding: 0.1rem 0.4rem;
  cursor: pointer;
}

.mnt-active-filters__tag .mdi {
  font-size: 10px;
}

.mnt-active-filters__clear {
  background: none;
  border: none;
  color: #3b82f6;
  font-family: var(--font-body);
  font-size: 10px;
  cursor: pointer;
  margin-left: auto;
}
.mnt-statusbar__manage-btn--active {
  border-color: #3b82f6;
  border-style: solid;
  color: #3b82f6;
  background: #eff6ff;
}
</style>